<style lang="postcss">
	.background-layer {
		position: absolute;
		top: 0;
		left: 0;
		top: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		will-change: background, opacity;
		background-size: cover;
	}
	.object-cover {
		object-fit: cover;
	}
</style>

<script lang="ts">
	import type { Writable } from "svelte/store";
	import { getImageSrc, getGradientString } from "$lib/helpers/functions";
	import { fade } from "$lib/transitions";
	import { getContext } from "svelte";

	export let data: BackgroundLayer[] = [];
	export let z_index = "-1";

	const timing = getContext<Writable<Timing>>("timing");
	$: duration = Math.min(400, $timing?.duration ?? 0);

	const paper = `<svg style="width: 100%; height: 100%" width="100%" height="100%">
  <defs>
    <filter id="roughpaper">
      <feTurbulence
        type="fractalNoise"
        baseFrequency="0.04"
        numOctaves="5"
        result="noise"></feTurbulence>
      <feDiffuseLighting
        lighting-color="#f9f7f1"
        diffuseConstant="1"
        surfaceScale="2"
        result="diffLight">
        <feDistantLight azimuth="45" elevation="35"></feDistantLight>
      </feDiffuseLighting>
    </filter>
  </defs>
  <rect
    filter="url(#roughpaper)"
    width="100%"
    height="100%"
    fill="none"></rect>
</svg>`;

	function adjustOpacity(opacity: number, min: number, max: number): number {
		return Math.round((opacity / 100) * (max - min) + min);
	}

	$: newData = data.map((layer, index) => {
		const background = getBackground(layer);
		let key = `${layer.type}-${Boolean(background.content)}-${
			background.background
		}-${index}`;
		if (layer.type === "color") {
			key = `${layer.type}-${Boolean(background.content)}-${index}`;
		}
		return {
			background: background.background,
			opacity: background.opacity,
			content: background.content,
			key: key
		};
	});

	function getBackground(layer: BackgroundLayer): {
		background: string;
		opacity: number;
		content: string;
	} {
		if (layer.type === "color") {
			return {
				background: `background-color: ${layer.color.color}`,
				opacity: layer.opacity / 100,
				content: ""
			};
		} else if (layer.type === "gradient") {
			const computedString = getGradientString(layer.gradient);
			return {
				background: `background-image: ${computedString}`,
				opacity: layer.opacity / 100,
				content: ""
			};
		} else if (layer.type === "image") {
			return {
				background: `background-image: url("${getImageSrc(
					layer.image.embed
				)}")`,
				opacity: layer.opacity / 100,
				content: ""
			};
		} else if (layer.type === "pattern") {
			const adjustedOpacity = adjustOpacity(layer.opacity, 1, 20);
			if (layer.pattern.type === "waves") {
				return {
					background: `background-image: repeating-radial-gradient( circle at 0 0, transparent 0, #ffffff 3px ), repeating-linear-gradient( #000000, #000000 )`,
					opacity: adjustedOpacity / 100,
					content: ""
				};
			} else if (layer.pattern.type === "paper") {
				return {
					background: "transparent",
					opacity: adjustedOpacity / 100,
					content: paper
				};
			} else if (layer.pattern.type === "watercolor") {
				return {
					background: `background-image: url("/images/textures/pastel.jpg"); filter: grayscale(100%)`,
					opacity: adjustedOpacity / 100,
					content: ""
				};
			} else if (layer.pattern.type === "lines") {
				return {
					background: `background: repeating-linear-gradient(45deg, #000000 0, #000000 0.5px, rgba(255, 255, 255, 1) 0, rgba(255, 255, 255, 1) 50%); background-size: 5px 5px;`,
					opacity: adjustedOpacity / 100,
					content: ""
				};
			} else if (layer.pattern.type === "dots") {
				return {
					background: `background: radial-gradient(rgba(255, 255, 255, 1) 0.5px, #000000 0); background-size: 4px 4px;`,
					opacity: adjustedOpacity / 100,
					content: ""
				};
			}
		} else {
			return {
				background: "transparent",
				opacity: 0,
				content: ""
			};
		}
		return {
			background: "transparent",
			opacity: 0,
			content: ""
		};
	}
</script>

{#each newData as layer (layer.key)}
	<div
		transition:fade="{{ duration: Math.min(400, $timing?.duration ?? 0) }}"
		class="background-layer {$$props.class ?? ''}"
		style="transition: background {duration}ms linear; z-index: {z_index}; opacity: {layer.opacity}; {layer.background}">
		{@html layer.content}
	</div>
{/each}
